import React from 'react';
import { useParams } from 'react-router-dom';
import { PageLayout } from '@/shared/layout';
import { useConstructionAutomation } from './hooks/useAutomation';
import { useAuth } from '@/core/auth/AuthContext';
import { Card, Badge, Button, Table, Switch } from '@/components/ui';

export default function AutomationListPage() {
  const { projectId } = useParams<{ projectId: string }>();
  const { profile } = useAuth();
  const companyId = profile?.company_id || '';

  const { rules, logs, isLoading, toggleRule } = useConstructionAutomation(companyId, projectId);

  const [activeTab, setActiveTab] = React.useState<'rules' | 'logs'>('rules');

  return (
    <PageLayout
      title="Tự động hóa Thi công"
      description="Thiết lập các quy tắc tự động hóa để tối ưu vận hành hiện trường và cảnh báo rủi ro."
      breadcrumbs={[{ label: 'Thi công', path: `/construction/${projectId}/plan` }, { label: 'Tự động hóa' }]}
      isLoading={isLoading}
      headerActions={
        <div className="flex gap-2">
          <Button variant="outline" icon="ri-template-line">Mẫu quy tắc</Button>
          <Button variant="primary" icon="ri-add-line">Thêm quy tắc mới</Button>
        </div>
      }
    >
      <div className="space-y-6">
        <div className="flex bg-white p-1 rounded-2xl border border-slate-100 shadow-sm w-fit mb-4">
          <button
            className={`px-6 py-2 rounded-xl text-xs font-bold transition-all ${activeTab === 'rules' ? 'bg-os-primary text-white shadow-lg' : 'text-slate-500 hover:bg-slate-50'}`}
            onClick={() => setActiveTab('rules')}
          >
            Quy tắc hoạt động
          </button>
          <button
            className={`px-6 py-2 rounded-xl text-xs font-bold transition-all ${activeTab === 'logs' ? 'bg-os-primary text-white shadow-lg' : 'text-slate-500 hover:bg-slate-50'}`}
            onClick={() => setActiveTab('logs')}
          >
            Nhật ký thực thi
          </button>
        </div>

        {activeTab === 'rules' ? (
          <div className="grid grid-cols-1 gap-4">
            {rules.map((rule) => (
              <Card key={rule.id} className="p-6 transition-all hover:border-os-primary/30">
                <div className="flex justify-between items-start gap-4">
                  <div className="flex gap-4">
                    <div className={`w-12 h-12 rounded-2xl flex items-center justify-center shrink-0 ${rule.is_enabled ? 'bg-os-primary/10 text-os-primary' : 'bg-slate-100 text-slate-400'}`}>
                      <i className={`${rule.trigger_code === 'qa_failed' ? 'ri-shield-check-line' : 'ri-notification-3-line'} text-2xl`} />
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <h4 className="font-bold text-slate-900">{rule.name}</h4>
                        <Badge variant="neutral" className="text-[9px] font-bold tracking-widest">{rule.trigger_code}</Badge>
                      </div>
                      <p className="text-xs text-slate-500 italic mb-4">{rule.description}</p>

                      <div className="flex items-center gap-6">
                        <div className="flex items-center gap-1.5">
                          <i className="ri-flashlight-line text-amber-500 text-sm" />
                          <span className="text-[10px] font-bold text-slate-400 tracking-tighter">Trigger:</span>
                          <span className="text-[11px] font-bold text-slate-700">{rule.trigger_code}</span>
                        </div>
                        <div className="flex items-center gap-1.5 text-os-primary">
                          <i className="ri-arrow-right-line text-sm" />
                          <span className="text-[10px] font-bold text-slate-400 tracking-tighter">Action:</span>
                          <span className="text-[11px] font-bold">{rule.action_code}</span>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="flex flex-col items-end gap-3">
                    <div className="flex items-center gap-2">
                      <span className="text-[10px] font-bold text-slate-400">{rule.is_enabled ? 'Đang bật' : 'Đang tắt'}</span>
                      <Switch
                        checked={rule.is_enabled}
                        onCheckedChange={(checked) => toggleRule({ id: rule.id, isEnabled: checked })}
                      />
                    </div>
                    <div className="flex gap-2">
                      <Button variant="ghost" size="sm" icon="ri-edit-line" />
                      <Button variant="ghost" size="sm" icon="ri-delete-bin-line" className="text-red-400 hover:text-red-500" />
                    </div>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        ) : (
          <Card className="overflow-hidden border-none shadow-sm">
            <Table
              columns={[
                { key: 'time', title: 'Thời gian', className: 'w-[150px]' },
                { key: 'rule', title: 'Quy tắc' },
                { key: 'status', title: 'Trạng thái', align: 'center' },
                { key: 'result', title: 'Kết quả / Lỗi' },
              ]}
              data={logs}
              isLoading={isLoading}
              renderCell={(item, key) => {
                switch (key) {
                  case 'time':
                    return <span className="text-[10px] text-slate-400 font-mono italic">{new Date(item.executed_at).toLocaleString('vi-VN')}</span>;
                  case 'rule':
                    return <span className="text-xs font-bold text-slate-900">{(item as any).rule?.name || 'Unknown Rule'}</span>;
                  case 'status':
                    return (
                      <Badge variant={item.status === 'success' ? 'success' : 'danger'} className="text-[9px] font-bold">
                        {item.status}
                      </Badge>
                    );
                  case 'result':
                    return <span className="text-[10px] text-slate-500 italic">{item.error_message || 'Thực thi thành công'}</span>;
                  default:
                    return null;
                }
              }}
            />
          </Card>
        )}
      </div>
    </PageLayout>
  );
}
